<template>
  <!-- Прокси — единственный канал к AI API. Когда он молча умирает, отваливаются
       чат и обогащение; виджет показывает это сразу, а не через жалобы. -->
  <div
    v-if="isAdmin"
    class="proxy-status"
    :class="stateClass"
    v-tooltip.bottom="tooltip"
  >
    <i :class="stateIcon" />
    <span class="proxy-status__label">{{ label }}</span>
    <span v-if="status && status.ok && status.latency_ms" class="proxy-status__latency">
      {{ status.latency_ms }} мс
    </span>
    <Button
      icon="pi pi-refresh"
      class="p-button-text p-button-rounded p-button-sm proxy-status__refresh"
      :loading="loading"
      aria-label="Проверить прокси заново"
      @click="check(true)"
    />
  </div>
</template>

<script>
import Button from 'primevue/button'
import apiService from '../services/apiService'

// Прокси проверяется реальным запросом, на бэкенде стоит кэш на 60 с —
// опрашиваем в том же темпе, чаще смысла нет.
const POLL_INTERVAL_MS = 60000

export default {
  name: 'ProxyStatusWidget',
  components: { Button },
  data() {
    return {
      status: null,
      loading: false,
      failed: false,
      timer: null
    }
  },
  computed: {
    isAdmin() {
      try {
        const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
        return userInfo.role === 'admin'
      } catch {
        return false
      }
    },
    stateClass() {
      if (this.failed) return 'proxy-status--unknown'
      if (!this.status) return 'proxy-status--unknown'
      if (!this.status.configured) return 'proxy-status--off'
      if (!this.status.ok) return 'proxy-status--fail'
      return this.status.slow ? 'proxy-status--slow' : 'proxy-status--ok'
    },
    stateIcon() {
      if (this.loading) return 'pi pi-spin pi-spinner'
      if (this.failed || !this.status) return 'pi pi-question-circle'
      if (!this.status.configured) return 'pi pi-minus-circle'
      if (!this.status.ok) return 'pi pi-times-circle'
      return this.status.slow ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'
    },
    label() {
      if (this.failed) return 'Прокси: нет данных'
      if (!this.status) return 'Прокси: проверка…'
      if (!this.status.configured) return 'Прокси не настроен'
      if (!this.status.ok) return 'Прокси не работает'
      return this.status.slow ? 'Прокси тормозит' : 'Прокси работает'
    },
    tooltip() {
      if (this.failed) return 'Не удалось получить статус прокси от сервера'
      if (!this.status) return 'Проверяем соединение…'

      const lines = []
      if (this.status.endpoint) lines.push(`Адрес: ${this.status.endpoint}`)
      if (this.status.error) lines.push(this.status.error)
      if (this.status.ok && this.status.slow) {
        lines.push('Канал живой, но вялый — AI-запросы могут отваливаться по таймауту')
      } else if (this.status.ok) {
        lines.push('AI-функции (чат, обогащение) доступны')
      } else if (this.status.configured) {
        lines.push('AI-функции сейчас работать не будут')
      }
      if (this.status.checked_at) {
        lines.push(`Проверено: ${new Date(this.status.checked_at).toLocaleTimeString('ru-RU')}`)
      }
      return lines.join('\n')
    }
  },
  mounted() {
    if (!this.isAdmin) return
    this.check()
    this.timer = setInterval(() => this.check(), POLL_INTERVAL_MS)
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async check(force = false) {
      this.loading = true
      try {
        const response = await apiService.getProxyStatus(force)
        this.status = response.data
        this.failed = false
      } catch (error) {
        console.error('Не удалось получить статус прокси:', error)
        this.failed = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.proxy-status {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.25rem 0.25rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 2rem;
  font-size: 0.875rem;
  line-height: 1;
  white-space: nowrap;
}

.proxy-status__label {
  font-weight: 500;
}

.proxy-status__latency {
  color: var(--text-color-secondary);
  font-variant-numeric: tabular-nums;
}

.proxy-status__refresh {
  width: 1.75rem;
  height: 1.75rem;
  color: inherit;
}

.proxy-status--ok {
  background: var(--green-50);
  border-color: var(--green-200);
  color: var(--green-700);
}

.proxy-status--slow {
  background: var(--yellow-50);
  border-color: var(--yellow-200);
  color: var(--yellow-800);
}

.proxy-status--fail {
  background: var(--red-50);
  border-color: var(--red-200);
  color: var(--red-700);
}

.proxy-status--off,
.proxy-status--unknown {
  background: var(--surface-100);
  color: var(--text-color-secondary);
}
</style>
